import { useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  AdminCallNotesPage,
  AdminCallNoteItem,
} from '@karehero/llama/pwa/pages/AdminCallNotesPage/AdminCallNotesPage';

import { DocumentTagID, OrganiseDocumentStatus } from '@karehero/models';
import { useGoBack } from 'hooks/useGoBack';

import type { CareCircle } from '@karehero/models';
import { getAccountCareCircles } from 'store/slices/careCircleSlice';
import {
  useListOrganiseDocumentsQuery,
  useDeleteOrganiseDocumentMutation,
} from 'store/api/organiseDocument';

import { DYNAMIC_ROUTES } from 'router/routes';

export const AdminCallNotes = () => {
  const goBack = useGoBack();
  const navigate = useNavigate();
  const { careCircleID } = useParams<{ careCircleID: string }>();
  const careCircles = useSelector(getAccountCareCircles) as CareCircle[] | null;

  const careCircle = careCircles?.find((cc) => cc.id === careCircleID);
  const careCircleName = careCircle?.careRecipientAccount
    ? `${careCircle.careRecipientAccount.firstName} ${careCircle.careRecipientAccount.lastName}`
    : undefined;

  const { data: documents = [], isLoading } = useListOrganiseDocumentsQuery(
    {
      careCircleId: careCircleID ?? '',
      includeTags: [DocumentTagID.CallNotes],
    },
    { skip: !careCircleID },
  );

  const [deleteDocument] = useDeleteOrganiseDocumentMutation();

  const callNotes: AdminCallNoteItem[] = useMemo(
    () =>
      documents
        .filter((doc) => doc.status !== OrganiseDocumentStatus.Pending)
        .map((doc) => ({
          id: doc.id,
          title: doc.title,
          createdAt: doc.createdAt,
        })),
    [documents],
  );

  const handleAddNew = useCallback(() => {
    if (careCircleID) {
      navigate(DYNAMIC_ROUTES.ADMIN_CALL_NOTE_NEW_FOR_CIRCLE(careCircleID));
    }
  }, [careCircleID, navigate]);

  const handleView = useCallback(
    (note: AdminCallNoteItem) => {
      if (careCircleID) {
        navigate(DYNAMIC_ROUTES.ADMIN_CALL_NOTE_VIEW(careCircleID, note.id));
      }
    },
    [careCircleID, navigate],
  );

  const handleEdit = useCallback(
    (note: AdminCallNoteItem) => {
      if (careCircleID) {
        navigate(DYNAMIC_ROUTES.ADMIN_CALL_NOTE_EDIT(careCircleID, note.id));
      }
    },
    [careCircleID, navigate],
  );

  const handleDelete = useCallback(
    async (note: AdminCallNoteItem) => {
      if (!careCircleID) return;

      try {
        await deleteDocument({
          careCircleId: careCircleID,
          documentId: note.id,
        }).unwrap();
      } catch (error) {
        console.error('Delete failed:', error);
      }
    },
    [careCircleID, deleteDocument],
  );

  return (
    <AdminCallNotesPage
      careCircleName={careCircleName}
      callNotes={callNotes}
      isLoading={isLoading}
      onBack={goBack}
      onAddNew={handleAddNew}
      onView={handleView}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );
};

export default AdminCallNotes;
